import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DataTableShell, FilterBar, PageHeader, useInfinitePage } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { formatDate, formatPaise, PAYOUT_STATUS_LABELS, payoutStatusColor } from "../../lib/format";
import type { AdminPayout } from "../../types/api";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "released", label: "Released" },
  { value: "paid", label: "Paid" },
  { value: "on_hold", label: "On hold" },
];
const PAYEE_TABS = [
  { value: "recruiter", label: "Independent recruiters" },
  { value: "consultancy", label: "Consultancies" },
] as const;
const PAYEE_TAB_DEFAULT = PAYEE_TABS[0].value;
const PAYOUT_STATUS_DEFAULT = STATUS_TABS[0].value;
const PAYOUT_FILTER_DEFAULTS = { payee: PAYEE_TAB_DEFAULT, status: PAYOUT_STATUS_DEFAULT };
const PAYOUT_FILTER_ALLOWED = {
  payee: PAYEE_TABS.map((item) => item.value),
  status: STATUS_TABS.map((item) => item.value),
};
const PAYOUT_FILTER_OPTIONS = { allowed: PAYOUT_FILTER_ALLOWED };

function payeeKey(payout: AdminPayout): string {
  return payout.payee_type === "consultancy"
    ? `c:${payout.payee_company_id ?? payout.id}`
    : `u:${payout.payee_user_id ?? payout.recruiter_id ?? payout.id}`;
}

export function AdminPayoutsPage() {
  const queryClient = useQueryClient();
  const payoutFilters = useQueryFilters(PAYOUT_FILTER_DEFAULTS, PAYOUT_FILTER_OPTIONS);
  const payeeTab = payoutFilters.values.payee as (typeof PAYEE_TABS)[number]["value"];
  const statusTab = payoutFilters.values.status;
  const [selected, setSelected] = useState<string[]>([]);
  const [payOpen, setPayOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState<AdminPayout | null>(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [singlePayId, setSinglePayId] = useState<string | null>(null);

  const statusFilter = statusTab === "all" ? undefined : statusTab;

  const payoutsQuery = useInfinitePage(["admin-payouts", payeeTab, statusFilter], (page, pageSize) =>
    adminApi.listPayouts({
      payee_type: payeeTab,
      status: statusFilter,
      page,
      page_size: pageSize,
    }),
  );

  const payouts = payoutsQuery.items;
  const groups = useMemo(() => {
    const map = new Map<string, AdminPayout[]>();
    for (const payout of payouts ?? []) {
      const key = payeeKey(payout);
      const rows = map.get(key) ?? [];
      rows.push(payout);
      map.set(key, rows);
    }
    return [...map.values()];
  }, [payouts]);
  const payoutRows = payouts ?? [];

  const unpaidSelected = selected.filter((id) => payoutRows.find((row) => row.id === id)?.status !== "paid");

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    setSelected([]);
  }

  const markPaidMutation = useMutation({
    mutationFn: async () => {
      const ids = singlePayId ? [singlePayId] : unpaidSelected;
      if (ids.length === 1) {
        return adminApi.markPayoutPaid(ids[0], { payment_reference: paymentReference.trim() || undefined });
      }
      return adminApi.bulkMarkPayoutsPaid({
        payout_ids: ids,
        payment_reference: paymentReference.trim(),
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "Payment issued" });
      setPayOpen(false);
      setSinglePayId(null);
      setPaymentReference("");
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Could not mark paid",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  const supplierMutation = useMutation({
    mutationFn: () =>
      adminApi.setSupplierInvoice(invoiceOpen!.id, { supplier_invoice_ref: supplierRef.trim() }),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Supplier invoice recorded" });
      setInvoiceOpen(null);
      setSupplierRef("");
      invalidate();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Could not save invoice",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  function toggleRow(id: string, checked: boolean) {
    setSelected((current) => (checked ? [...current, id] : current.filter((value) => value !== id)));
  }

  function toggleGroup(rows: AdminPayout[], checked: boolean) {
    const ids = rows.filter((row) => row.status !== "paid").map((row) => row.id);
    setSelected((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return [...next];
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.payouts}
        title="Outgoing payouts"
        subtitle="Pay independent recruiters and consultancies after client collections. Supplier GST invoices can be recorded for consultancies."
        actions={
          unpaidSelected.length > 0 ? (
            <Button
              onClick={() => {
                setSinglePayId(null);
                setPaymentReference("");
                setPayOpen(true);
              }}
            >
              Mark payment issued ({unpaidSelected.length})
            </Button>
          ) : undefined
        }
      />

      <FilterBar>
        <Tabs
          value={payeeTab}
          onChange={(value) => {
            if (!value) return;
            payoutFilters.set({ payee: value });
            setSelected([]);
          }}
        >
          <Tabs.List>
            {PAYEE_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Tabs
          value={statusTab}
          onChange={(value) => {
            if (!value) return;
            payoutFilters.set({ status: value });
            setSelected([]);
          }}
        >
          <Tabs.List>
            {STATUS_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={payoutsQuery.isLoading}
        error={
          payoutsQuery.isError
            ? payoutsQuery.error instanceof Error
              ? payoutsQuery.error.message
              : "Could not load payouts"
            : undefined
        }
        onRetry={() => void payoutsQuery.refetch()}
        hasMore={payoutsQuery.hasMore}
        loadingMore={payoutsQuery.loadingMore}
        onLoadMore={payoutsQuery.loadMore}
        isEmpty={payoutRows.length === 0}
        empty={{
          icon: NAV_ICONS.payouts,
          title: "No payouts in this view",
          description: "Payouts are created automatically when a hire is confirmed.",
        }}
      >
        <Stack gap="md" p="md">
          {groups.map((rows) => {
            const payee = rows[0];
            const unpaid = rows.filter((row) => row.status !== "paid");
            const allSelected = unpaid.length > 0 && unpaid.every((row) => selected.includes(row.id));
            const total = rows.reduce((sum, row) => sum + row.net_paise, 0);
            return (
              <Paper key={payeeKey(payee)} withBorder p="md">
                <Group justify="space-between" mb="sm">
                  <div>
                    <Text fw="var(--axio-font-weight-semibold)">{payee.payee_name}</Text>
                    <Text size="sm" c="dimmed">
                      {rows.length} payout{rows.length === 1 ? "" : "s"} · {formatPaise(total)}
                    </Text>
                  </div>
                  {unpaid.length > 0 && (
                    <Checkbox
                      label="Select unpaid"
                      checked={allSelected}
                      onChange={(event) => toggleGroup(rows, event.currentTarget.checked)}
                    />
                  )}
                </Group>
                <Table verticalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w="var(--axio-unit-36)" />
                      <Table.Th>Candidate / role</Table.Th>
                      <Table.Th>Client</Table.Th>
                      <Table.Th>Net</Table.Th>
                      <Table.Th>Scheduled</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Reference</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((payout) => (
                      <Table.Tr key={payout.id}>
                        <Table.Td>
                          {payout.status !== "paid" && (
                            <Checkbox
                              checked={selected.includes(payout.id)}
                              onChange={(event) => toggleRow(payout.id, event.currentTarget.checked)}
                            />
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{payout.placement.candidate_name}</Text>
                          <Text size="xs" c="dimmed">
                            {payout.placement.job_title}
                          </Text>
                        </Table.Td>
                        <Table.Td>{payout.placement.company_name}</Table.Td>
                        <Table.Td>{formatPaise(payout.net_paise)}</Table.Td>
                        <Table.Td>{formatDate(payout.scheduled_for)}</Table.Td>
                        <Table.Td>
                          <Badge color={payoutStatusColor(payout.status)} variant="light">
                            {PAYOUT_STATUS_LABELS[payout.status] ?? payout.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{payout.payment_reference || payout.transfer_reference || "—"}</Text>
                          {payout.supplier_invoice_ref && (
                            <Text size="xs" c="dimmed">
                              Bill {payout.supplier_invoice_ref}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" justify="flex-end">
                            {payeeTab === "consultancy" && (
                              <Button
                                size="xs"
                                variant="subtle"
                                onClick={() => {
                                  setSupplierRef(payout.supplier_invoice_ref ?? "");
                                  setInvoiceOpen(payout);
                                }}
                              >
                                Record invoice
                              </Button>
                            )}
                            {payout.status !== "paid" && (
                              <Button
                                size="xs"
                                variant="light"
                                onClick={() => {
                                  setSinglePayId(payout.id);
                                  setPaymentReference("");
                                  setPayOpen(true);
                                }}
                              >
                                Mark paid
                              </Button>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            );
          })}
        </Stack>
      </DataTableShell>

      <Modal opened={payOpen} onClose={() => setPayOpen(false)} title="Mark payment issued" centered>
        <Stack>
          <Text size="sm">Enter the bank transfer / NEFT reference for this payout.</Text>
          <TextInput
            label="Payment reference"
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPayOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={markPaidMutation.isPending}
              disabled={!paymentReference.trim()}
              onClick={() => markPaidMutation.mutate()}
            >
              Mark issued
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={invoiceOpen != null} onClose={() => setInvoiceOpen(null)} title="Record supplier invoice" centered>
        <Stack>
          <Text size="sm">GST invoice number from the consultancy, used for input credit linkage.</Text>
          <TextInput
            label="Supplier invoice number"
            value={supplierRef}
            onChange={(event) => setSupplierRef(event.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setInvoiceOpen(null)}>
              Cancel
            </Button>
            <Button
              loading={supplierMutation.isPending}
              disabled={!supplierRef.trim()}
              onClick={() => supplierMutation.mutate()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
